import * as Sentry from '@sentry/react'
import { useForm } from '@tanstack/react-form'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  Textarea
} from '@repo/ui'
import { CircleAlert, FileText, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { z } from 'zod'
import type { RagFile } from '@/api/content/file'
import { addRagFile, deleteRagFile, updateRagFile } from '@/api/content/file'
import type { RagText } from '@/api/content/text'
import { createRagText, deleteRagText, updateRagText } from '@/api/content/text'
import { zodFieldValidator } from '@/common/parseDataType'
import { ContentTagPicker } from './ContentTagPicker'
import type { ChangeEvent, ReactNode } from 'react'

const ragTextFieldSchema = z.string().min(1, 'Text is required')

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

export function FileFormDialog({
  open,
  item,
  projectId,
  onSaved,
  onClose
}: {
  open: boolean
  item?: RagFile
  projectId?: number
  onSaved: () => void | Promise<void>
  onClose: () => void
}) {
  const isEditing = !!item
  const [creatingTag, setCreatingTag] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([])
  const [uploadingFileIndex, setUploadingFileIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm({
    defaultValues: { description: item?.description ?? '', fileName: '', tags: item?.tags ?? [] },
    onSubmit: async ({ value }) => {
      if (creatingTag) return
      setSubmitError(null)
      try {
        if (item) {
          await updateRagFile(
            item.fileId,
            value.description,
            projectId,
            value.tags.map((tag) => tag.tagId)
          )
        } else {
          if (selectedFiles.length === 0) {
            setSubmitError('Please select at least one file')
            return
          }
          for (const [index, file] of selectedFiles.entries()) {
            setUploadingFileIndex(index)
            await addRagFile(
              file,
              value.description,
              projectId,
              value.tags.map((tag) => tag.tagId)
            )
          }
        }
        form.reset()
        setSelectedFiles([])
        setUploadingFileIndex(null)
        await onSaved()
        onClose()
      } catch (err) {
        Sentry.captureException(err, { extra: { projectId, ragFileId: item?.fileId } })
        setUploadingFileIndex(null)
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  function handleClose() {
    if (creatingTag || form.state.isSubmitting) return
    form.reset()
    setSelectedFiles([])
    setUploadingFileIndex(null)
    setSubmitError(null)
    onClose()
  }

  function handleFileSelection(event: ChangeEvent<HTMLInputElement>) {
    setSelectedFiles(Array.from(event.currentTarget.files ?? []))
    setSubmitError(null)
    event.currentTarget.value = ''
  }

  function removeSelectedFile(index: number) {
    setSelectedFiles((files) => files.filter((_, fileIndex) => fileIndex !== index))
    setSubmitError(null)
  }

  const uploadActionLabel =
    selectedFiles.length === 0
      ? 'Upload files'
      : `Upload ${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}`

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl min-w-0 overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit File' : 'Upload Files'}</DialogTitle>
          <DialogDescription className={isEditing ? 'sr-only' : undefined}>
            {isEditing
              ? 'Edit file description'
              : 'Select one or more files. Description and tags apply to every file.'}
          </DialogDescription>
        </DialogHeader>

        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
          className="min-w-0 space-y-4"
        >
          {!isEditing && (
            <Field className="min-w-0">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelection}
                disabled={form.state.isSubmitting || creatingTag}
                className="hidden"
              />
              <div className="flex flex-col gap-3 rounded-lg border border-dashed bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-xs">
                    <Upload aria-hidden="true" className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Choose files from your device</p>
                    <p className="text-xs text-muted-foreground">You can select multiple files at once.</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={form.state.isSubmitting || creatingTag}
                >
                  {selectedFiles.length === 0 ? 'Choose files' : 'Choose again'}
                </Button>
              </div>

              {selectedFiles.length > 0 && (
                <div className="min-w-0 overflow-hidden rounded-lg border" aria-live="polite">
                  <div className="flex items-center justify-between gap-3 bg-muted/40 px-3 py-2">
                    <p className="text-sm font-medium">
                      {selectedFiles.length} {selectedFiles.length === 1 ? 'file selected' : 'files selected'}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-muted-foreground"
                      onClick={() => {
                        setSelectedFiles([])
                        setSubmitError(null)
                      }}
                      disabled={form.state.isSubmitting || creatingTag}
                    >
                      Clear all
                    </Button>
                  </div>
                  <ul aria-label="Selected files" className="max-h-44 min-w-0 divide-y overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                        className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5"
                      >
                        <FileText aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="truncate text-sm font-medium" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => removeSelectedFile(index)}
                          disabled={form.state.isSubmitting || creatingTag}
                        >
                          <X aria-hidden="true" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Field>
          )}

          <form.Field name="description">
            {(field) => (
              <Field>
                <Textarea
                  placeholder="Description (optional)"
                  rows={4}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting || creatingTag}
                  className="resize-none"
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <form.Field name="tags">
                {(field) => (
                  <ContentTagPicker
                    value={field.state.value}
                    onChange={field.handleChange}
                    disabled={isSubmitting || creatingTag}
                    creating={creatingTag}
                    onCreatingChange={setCreatingTag}
                  />
                )}
              </form.Field>
            )}
          </form.Subscribe>

          <DialogFooter className="min-w-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={form.state.isSubmitting || creatingTag}
            >
              Cancel
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting || creatingTag}>
                  {isSubmitting
                    ? isEditing
                      ? 'Saving...'
                      : uploadingFileIndex === null
                        ? 'Preparing upload...'
                        : `Uploading ${uploadingFileIndex + 1} of ${selectedFiles.length}...`
                    : isEditing
                      ? 'Save'
                      : uploadActionLabel}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function TextFormDialog({
  open,
  item,
  projectId,
  onSaved,
  onClose
}: {
  open: boolean
  item?: RagText
  projectId?: number
  onSaved: () => void | Promise<void>
  onClose: () => void
}) {
  const isEditing = !!item
  const [creatingTag, setCreatingTag] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { text: item?.text ?? '', tags: item?.tags ?? [] },
    onSubmit: async ({ value }) => {
      if (creatingTag) return
      setSubmitError(null)
      try {
        if (item) {
          await updateRagText(
            item.docId,
            value.text,
            projectId,
            value.tags.map((tag) => tag.tagId)
          )
        } else {
          await createRagText(
            value.text,
            projectId,
            value.tags.map((tag) => tag.tagId)
          )
        }
        form.reset()
        await onSaved()
        onClose()
      } catch (err) {
        Sentry.captureException(err, { extra: { projectId, ragTextDocId: item?.docId } })
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  function handleClose() {
    if (creatingTag || form.state.isSubmitting) return
    form.reset()
    setSubmitError(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Text' : 'Add Text'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEditing ? 'Edit existing text content' : 'Add new text to your content vault'}
          </DialogDescription>
        </DialogHeader>

        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <form.Field name="text" validators={{ onChange: zodFieldValidator(ragTextFieldSchema) }}>
                {(field) => (
                  <Field>
                    <Textarea
                      placeholder="Enter the text to add"
                      rows={isEditing ? 16 : 10}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={isSubmitting || creatingTag}
                      className="field-sizing-fixed resize-none"
                    />
                    <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
                  </Field>
                )}
              </form.Field>
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <form.Field name="tags">
                {(field) => (
                  <ContentTagPicker
                    value={field.state.value}
                    onChange={field.handleChange}
                    disabled={isSubmitting || creatingTag}
                    creating={creatingTag}
                    onCreatingChange={setCreatingTag}
                  />
                )}
              </form.Field>
            )}
          </form.Subscribe>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={form.state.isSubmitting || creatingTag}
            >
              Cancel
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting || creatingTag}>
                  {isSubmitting ? (isEditing ? 'Saving...' : 'Adding...') : isEditing ? 'Save' : 'Add'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function FileDeleteDialog({
  item,
  blockingAssignments,
  projectId,
  notice,
  onDeleted,
  onClose
}: {
  item: RagFile | null
  blockingAssignments?: Array<{ label: string; canManage: boolean }>
  projectId?: number
  notice?: ReactNode
  onDeleted: () => void | Promise<void>
  onClose: () => void
}) {
  const [isPending, setIsPending] = useState(false)
  const [deleteError, setDeleteError] = useState<{ fileId: number; message: string } | null>(null)
  const error = deleteError && deleteError.fileId === item?.fileId ? deleteError.message : null
  const assignments = blockingAssignments ?? []
  const isBlocked = assignments.length > 0
  const requiresAdmin = assignments.some((assignment) => !assignment.canManage)

  async function handleConfirm() {
    if (!item || isBlocked) return
    setIsPending(true)
    setDeleteError(null)
    try {
      await deleteRagFile(item.fileId, projectId)
      await onDeleted()
      setDeleteError(null)
      onClose()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId, ragFileId: item.fileId } })
      setDeleteError({ fileId: item.fileId, message: err instanceof Error ? err.message : 'An error occurred' })
    } finally {
      setIsPending(false)
    }
  }

  function handleClose() {
    if (isPending) return
    setDeleteError(null)
    onClose()
  }

  return (
    <Dialog
      open={!!item}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isBlocked ? "This file can't be deleted" : 'Delete file?'}</DialogTitle>
          <DialogDescription>
            {isBlocked ? (
              <>
                <span className="font-medium text-foreground">{item?.fileName}</span> is currently used as a default
                document template.
              </>
            ) : (
              <>
                Delete <span className="font-medium text-foreground">{item?.fileName}</span>? This action cannot be
                undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {isBlocked && (
          <div className="flex gap-3 rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            <div className="space-y-2">
              <div>
                <p className="font-medium">Used as</p>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                  {assignments.map((assignment) => (
                    <li key={assignment.label}>{assignment.label}</li>
                  ))}
                </ul>
              </div>
              <p className="text-muted-foreground">
                {requiresAdmin
                  ? 'Ask an organisation administrator to change the default template before deleting this file.'
                  : 'Remove this file as the default or choose another default template before deleting it.'}
              </p>
            </div>
          </div>
        )}
        {error && !isBlocked && (
          <div role="alert" className="flex gap-3 rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium text-destructive">{"Couldn't delete file"}</p>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        )}
        {notice}
        <DialogFooter>
          {isBlocked ? (
            <Button size="sm" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button variant="destructive" size="sm" onClick={handleConfirm} disabled={isPending}>
                {isPending ? 'Deleting...' : 'Delete file'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function TextDeleteDialog({
  item,
  projectId,
  notice,
  onDeleted,
  onClose
}: {
  item: RagText | null
  projectId?: number
  notice?: ReactNode
  onDeleted: () => void | Promise<void>
  onClose: () => void
}) {
  const [isPending, setIsPending] = useState(false)
  const [deleteError, setDeleteError] = useState<{ docId: number; message: string } | null>(null)
  const error = deleteError && deleteError.docId === item?.docId ? deleteError.message : null

  async function handleConfirm() {
    if (!item) return
    setIsPending(true)
    setDeleteError(null)
    try {
      await deleteRagText(item.docId, projectId)
      await onDeleted()
      setDeleteError(null)
      onClose()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId, ragTextDocId: item.docId } })
      setDeleteError({ docId: item.docId, message: err instanceof Error ? err.message : 'An error occurred' })
    } finally {
      setIsPending(false)
    }
  }

  function handleClose() {
    setDeleteError(null)
    onClose()
  }

  return (
    <Dialog
      open={!!item}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Text</DialogTitle>
          <DialogDescription className="sr-only">Delete Text Modal</DialogDescription>
        </DialogHeader>
        <p className="text-sm">Are you sure you want to delete this text? This action cannot be undone.</p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {notice}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
